import { Box } from '@wix/design-system';
import { WidgetSize } from '@wix/wix-chatbot-common/browser';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import { useParams } from 'react-router-dom';

import { logSingleArticlePresented } from '../../bi';
import { Loader } from '../../common/components/Loader';
import { useAnimation } from '../../common/hooks/useAnimation';
import { useFetchArticle } from '../../common/hooks/useFetchArticle';
import { useOnWidgetExpanded } from '../../common/hooks/useOnWidgetExpanded';
import { selectArticleState } from '../../common/selectors/helpCenter';
import { experiments } from '../../experiments';
import { ArticleContainer } from '../../help-center/components/ArticleContainer';
import { Subtitle } from '../../help-center/components/Subtitle';
import { widgetSDK } from '../../sdk/widgetSDK';
import s from './styles.scss';

type ArticleProps = {
  openCategoriesExternally?: boolean;
};

const widgetMaximizationEnabled = experiments.is('specs.helpwidget.widgetMaximization', 'true');

export const Article: FC<ArticleProps> = ({ openCategoriesExternally }) => {
  const store = useStore();
  const { articleId, articleUri } = useParams();
  const { article, error } = useSelector(selectArticleState);
  const [subtitleText, setSubtitleText] = useState<undefined | string>(undefined);

  const activeAnimation = useAnimation(s.moveInRight, s.moveInLeft, [articleId, articleUri]);

  useFetchArticle();

  const onTitleHiddenChange = useCallback(
    (value: boolean) => setSubtitleText(value ? article?.title : undefined),
    [article],
  );

  if (error) {
    throw error;
  }

  useOnWidgetExpanded((expanded: boolean) => {
    if (expanded && article) {
      logSingleArticlePresented(store.getState(), article);
    }
  });

  useEffect(() => {
    if (!widgetMaximizationEnabled) {
      return;
    }

    // When the widget opens on an article page: it will automatically increase in size
    widgetSDK.setWidgetSize({ size: WidgetSize.Maximized });

    return () => {
      // Navigating away from an article page: widget will revert to its original size
      widgetSDK.setWidgetSize({ size: WidgetSize.Default });
    };
  }, []);

  return (
    <Box height="100%" width="100%" direction="vertical" className={activeAnimation}>
      {article ? (
        <>
          <Subtitle
            text={subtitleText}
            href={article.url}
            articleId={article.id}
            showIconButton={!widgetMaximizationEnabled}
          />
          <ArticleContainer
            article={article}
            onTitleHiddenChange={onTitleHiddenChange}
            openCategoriesExternally={openCategoriesExternally}
          />
        </>
      ) : (
        <Loader dataHook="article__loader" height="100%" verticalAlign="middle" />
      )}
    </Box>
  );
};
